'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { createClient } from '@/lib/supabase/client';
import { useChat } from '@/lib/hooks/useChat';
import SupabaseStatusDot from '@/components/common/SupabaseStatusDot';
import Row1Hero from './Row1Hero';
import Row2LiveOps from './Row2LiveOps';
import Row2_5NewBusinessAndCrew from './Row2_5NewBusinessAndCrew';
import Row3Map from './Row3Map';
import Row4PipelineProduction from './Row4PipelineProduction';
import type { PipelineJob } from './Row4PipelineProduction';
import Row4_5OperationalBottlenecks from './Row4_5OperationalBottlenecks';
import type { BottlenecksPayload } from './Row4_5OperationalBottlenecks';
import Row5ExceptionsPeople from './Row5ExceptionsPeople';
import type { EscalatedTicket } from './Row5ExceptionsPeople';
import Row6SalesHandoff from './Row6SalesHandoff';

const OPS_AI_CACHE_KEY = 'openbooq_ops_ai_summary';

const getInitials = (first: string, last: string) =>
  `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();

export default function OperationsCommandCenterInteractive() {
  const { currentUser } = useAuth();
  const { impersonatedUser } = useImpersonation();
  const supabase = createClient();

  const [escalations, setEscalations] = useState<EscalatedTicket[]>([]);
  const [jobs, setJobs] = useState<PipelineJob[]>([]);
  const [mounted, setMounted] = useState(false);

  // ── Operational Bottlenecks (Row 4.5 + Row 4 footer counts) ───────────────
  const [bottlenecks, setBottlenecks] = useState<BottlenecksPayload | null>(null);
  const [bottlenecksLoading, setBottlenecksLoading] = useState(true);
  const [bottlenecksError, setBottlenecksError] = useState<string | null>(null);
  const bottlenecksAbortRef = useRef<AbortController | null>(null);
  const bottlenecksReloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── AI summary state (kept; rendered inside Row1Hero) ─────────────────────
  const [opsAiSummary, setOpsAiSummary] = useState<string | null>(null);
  const [opsAiLoading, setOpsAiLoading] = useState(false);
  const opsAiFetched = useRef(false);
  const {
    response: opsAiResponse,
    isLoading: opsAiChatLoading,
    sendMessage: sendOpsAiMessage,
  } = useChat('GEMINI', 'gemini/gemini-2.5-flash', false);

  useEffect(() => { setMounted(true); }, []);

  // Display name + initials respect View As impersonation.
  const displayUser = impersonatedUser ?? currentUser;
  const displayName = mounted ? displayUser.first_name || 'Alex' : 'Alex';
  const userInitials = mounted ? getInitials(displayUser.first_name || 'A', displayUser.last_name || 'R') : 'AR';

  // ── Fetch escalations + jobs from real tables ─────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      const [escalationsRes, jobsRes] = await Promise.all([
        supabase
          .from('escalations')
          .select('id, title, affected_job_id, priority, created_at, source_department')
          .eq('source_department', 'Operations')
          .neq('status', 'resolved')
          .order('created_at', { ascending: false })
          .limit(8),
        supabase
          .from('roofing_jobs')
          .select('job_id, customer_first_name, customer_last_name, city, state, status, assigned_crew')
          .in('status', ['active', 'scheduled', 'planning'])
          .order('created_at', { ascending: false })
          .limit(8),
      ]);

      const mappedEscalations: EscalatedTicket[] = (escalationsRes.data || []).map((e: any) => ({
        id: e.id,
        title: e.title,
        job: e.affected_job_id || '—',
        priority: e.priority,
        created_at: e.created_at,
        // assigned_to_name omitted — column does not exist yet; row renders "Unassigned".
      }));

      const mappedJobs: PipelineJob[] = (jobsRes.data || []).map((j: any) => ({
        id: j.job_id,
        customer: j.customer_last_name || j.customer_first_name || '—',
        address: `${j.city || ''}, ${j.state || ''}`.replace(/^, |, $/g, '') || '—',
        job_status: j.status === 'active' ? 'in-progress' : j.status,
        progress: j.status === 'active' ? 65 : j.status === 'scheduled' ? 15 : 5,
        crew: j.assigned_crew || 'Unassigned',
      }));

      setEscalations(mappedEscalations);
      setJobs(mappedJobs);
    } catch (err) {
      console.warn('[OpsCommandCenter] fetchData failed:', err);
      setEscalations([]);
      setJobs([]);
    }
  }, [supabase]);

  // ── Realtime: refetch on relevant table changes ───────────────────────────
  useEffect(() => {
    fetchData();
    const escalationsSub = supabase
      .channel('ops-escalations')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'escalations' }, fetchData)
      .subscribe();
    const jobsSub = supabase
      .channel('ops-roofing-jobs')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'roofing_jobs' }, fetchData)
      .subscribe();
    return () => {
      supabase.removeChannel(escalationsSub);
      supabase.removeChannel(jobsSub);
    };
  }, [fetchData, supabase]);

  // ── Operational Bottlenecks fetch (with retry + AbortController) ─────────
  const fetchBottlenecks = useCallback(async () => {
    bottlenecksAbortRef.current?.abort();
    const controller = new AbortController();
    bottlenecksAbortRef.current = controller;

    setBottlenecksLoading(true);
    setBottlenecksError(null);

    const maxAttempts = 3;
    let lastErr: unknown = null;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (controller.signal.aborted) return;
      try {
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Bottlenecks request timed out')), 8000),
        );
        const res = await Promise.race([
          fetch('/api/operations/bottlenecks', {
            method: 'GET',
            signal: controller.signal,
          }),
          timeoutPromise,
        ]);
        if (controller.signal.aborted) return;
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status}${text ? `: ${text}` : ''}`);
        }
        const json: BottlenecksPayload = await res.json();
        if (controller.signal.aborted) return;
        setBottlenecks(json);
        setBottlenecksError(null);
        setBottlenecksLoading(false);
        return;
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        lastErr = err;
        const msg = err instanceof Error ? err.message : String(err);
        const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
        const isTimeout = msg.includes('timed out');
        console.warn(`[Bottlenecks] attempt ${attempt + 1} failed: ${msg}`);
        if (attempt === maxAttempts - 1 || (!isRateLimit && !isTimeout)) break;
        const backoff = 1000 * Math.pow(2, attempt);
        await new Promise((r) => setTimeout(r, backoff));
      }
    }

    const msg = lastErr instanceof Error ? lastErr.message : String(lastErr ?? 'Unknown error');
    setBottlenecksError(msg);
    setBottlenecksLoading(false);
  }, []);

  // ── Bottlenecks: initial fetch + Realtime (debounced 500ms) ──────────────
  useEffect(() => {
    fetchBottlenecks();
    const scheduleReload = () => {
      if (bottlenecksReloadTimer.current) clearTimeout(bottlenecksReloadTimer.current);
      bottlenecksReloadTimer.current = setTimeout(() => { fetchBottlenecks(); }, 500);
    };
    const permitsSub = supabase
      .channel('ops-permits')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'permits' }, scheduleReload)
      .subscribe();
    const materialsSub = supabase
      .channel('ops-materials-alerts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'materials_alerts' }, scheduleReload)
      .subscribe();
    const changeOrdersSub = supabase
      .channel('ops-change-orders')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'change_orders' }, scheduleReload)
      .subscribe();
    return () => {
      bottlenecksAbortRef.current?.abort();
      if (bottlenecksReloadTimer.current) clearTimeout(bottlenecksReloadTimer.current);
      supabase.removeChannel(permitsSub);
      supabase.removeChannel(materialsSub);
      supabase.removeChannel(changeOrdersSub);
    };
  }, [fetchBottlenecks, supabase]);

  // ── Generate AI daily summary (cached per session) ────────────────────────
  const generateOpsAiSummary = useCallback(async () => {
    if (opsAiFetched.current) return;
    opsAiFetched.current = true;

    try {
      const cached = sessionStorage.getItem(OPS_AI_CACHE_KEY);
      if (cached) {
        const { text, ts } = JSON.parse(cached);
        if (Date.now() - ts < 30 * 60 * 1000) {
          setOpsAiSummary(text);
          return;
        }
      }
    } catch { /* ignore */ }

    setOpsAiLoading(true);
    try {
      const [jobsRes, escalationsRes, logisticsRes, crewsRes] = await Promise.all([
        supabase.from('roofing_jobs').select('status, contract_value, city, state').limit(30),
        supabase.from('escalations').select('priority, status, source_department').neq('status', 'resolved').limit(20),
        supabase.from('logistics_jobs').select('status').limit(20),
        supabase.from('installer_crews').select('compliance_status, num_crews, onboarding_step').limit(20),
      ]);

      const jobRows = jobsRes.data || [];
      const escalationRows = escalationsRes.data || [];
      const logisticsRows = logisticsRes.data || [];
      const crewRows = crewsRes.data || [];

      const activeJobs = jobRows.filter((j: any) => j.status === 'active');
      const delayedJobs = jobRows.filter((j: any) => j.status === 'delayed');
      const highPriorityEsc = escalationRows.filter((e: any) => e.priority === 'high' || e.priority === 'critical');
      const delayedLogistics = logisticsRows.filter((l: any) => l.status === 'delayed');
      const nonCompliantCrews = crewRows.filter((c: any) => c.compliance_status === 'non_compliant');
      const totalCrewCapacity = crewRows.reduce((s: number, c: any) => s + (c.num_crews || 0), 0);
      const deptBottlenecks = [...new Set(highPriorityEsc.map((e: any) => e.source_department))].filter(Boolean);

      const dataContext = `
Operations Command Center Live Data (${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}):
- Jobs: ${activeJobs.length} active installs, ${delayedJobs.length} delayed jobs.
- Escalations: ${escalationRows.length} open total, ${highPriorityEsc.length} high/critical. Bottlenecks: ${deptBottlenecks.join(', ') || 'None'}.
- Logistics: ${logisticsRows.length} jobs, ${delayedLogistics.length} delayed.
- Crews: ${crewRows.length} total, ${totalCrewCapacity} units. ${nonCompliantCrews.length} non-compliant.
- COO name: ${displayUser.first_name || 'COO'}
      `.trim();

      sendOpsAiMessage([
        {
          role: 'system',
          content: 'You are SmartPick™, an AI operations analyst for NextRoof 360, a roofing company. Generate a concise COO-level operations summary (3-4 sentences) based on the live data provided. Highlight operational anomalies, department bottlenecks, crew capacity risks, and provide 1-2 prioritized recommended actions. Be direct and data-driven. Write as flowing prose, no bullet points or headers.',
        },
        { role: 'user', content: dataContext },
      ], { max_completion_tokens: 280 });
    } catch (err) {
      console.warn('[OpsCommandCenter] AI summary fetch failed:', err);
      setOpsAiLoading(false);
      opsAiFetched.current = false;
    }
  }, [supabase, displayUser.first_name, sendOpsAiMessage]);

  useEffect(() => { generateOpsAiSummary(); }, [generateOpsAiSummary]);

  useEffect(() => {
    if (opsAiResponse && !opsAiChatLoading) {
      setOpsAiSummary(opsAiResponse);
      setOpsAiLoading(false);
      try {
        sessionStorage.setItem(OPS_AI_CACHE_KEY, JSON.stringify({ text: opsAiResponse, ts: Date.now() }));
      } catch { /* ignore */ }
    }
  }, [opsAiResponse, opsAiChatLoading]);

  const activeInstallsCount = jobs.filter((j) => j.job_status === 'in-progress').length;
  const isOpsAiLoading = opsAiLoading || opsAiChatLoading;

  return (
    <div className="min-h-screen" style={{ background: '#F8F9FA' }}>

      {/* ── Sticky Header chrome ──────────────────────────────────────────── */}
      <header
        className="sticky top-[60px] z-30 flex items-center justify-between px-6 py-3 border-b border-gray-200"
        style={{ background: '#FFFFFF', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
      >
        <div>
          <h1
            className="text-xl font-bold"
            style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}
          >
            Operations Command Center
          </h1>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-xs text-gray-400">Real-time operational oversight · All departments</p>
            <span className="text-gray-300 text-xs">·</span>
            <SupabaseStatusDot alertCount={escalations.length} />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-all hover:opacity-90 active:scale-95"
            style={{ background: '#07B0E9' }}
            suppressHydrationWarning
          >
            <Icon name="MegaphoneIcon" size={16} />
            Global Broadcast
          </button>
          <button
            className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-all"
            suppressHydrationWarning
          >
            <Icon name="ChatBubbleLeftRightIcon" size={20} />
          </button>
          <button
            className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-all relative"
            suppressHydrationWarning
          >
            <Icon name="BellIcon" size={20} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
          </button>
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)' }}
            suppressHydrationWarning
          >
            {userInitials}
          </div>
        </div>
      </header>

      {/* ── Content rows ──────────────────────────────────────────────────── */}
      <div className="px-6 py-6 space-y-6">
        <Row1Hero
          displayName={displayName}
          escalationsCount={escalations.length}
          activeInstallsCount={activeInstallsCount}
          aiSummary={opsAiSummary}
          aiLoading={isOpsAiLoading}
        />
        <Row2LiveOps />
        <Row2_5NewBusinessAndCrew />
        <Row3Map />
        <Row4PipelineProduction
          jobs={jobs}
          bottleneckCounts={bottlenecks?.counts ?? null}
          topBlockedPermit={(() => {
            if (!bottlenecks || bottlenecks.permits.length === 0) return null;
            const sorted = [...bottlenecks.permits].sort((a, b) => {
              const ad = new Date(a.filed_at ?? a.created_at).getTime();
              const bd = new Date(b.filed_at ?? b.created_at).getTime();
              return ad - bd;
            });
            const oldest = sorted[0];
            const anchorMs = new Date(oldest.filed_at ?? oldest.created_at).getTime();
            return {
              jurisdiction: oldest.jurisdiction_name ?? oldest.job_id,
              daysWaiting: Math.floor((Date.now() - anchorMs) / 86_400_000),
            };
          })()}
          bottlenecksLoading={bottlenecksLoading && !bottlenecks}
          bottlenecksError={bottlenecksError}
        />
        <Row4_5OperationalBottlenecks
          data={bottlenecks}
          loading={bottlenecksLoading}
          error={bottlenecksError}
          onRetry={fetchBottlenecks}
        />
        <Row5ExceptionsPeople escalations={escalations} />
        <Row6SalesHandoff />
      </div>
    </div>
  );
}
